// useFocusRestore — capture activeElement on open, restore on close.
//
// Audit finding A11Y.5: every modal / drawer in V3 dismisses without
// returning focus to the trigger element, leaving keyboard users
// stranded at <body>. This hook fixes it for any open/close-flagged
// surface with one line of caller code.
//
// Usage:
//   const open = useState(false);
//   useFocusRestore(open);  // pass the open boolean
//
// Captures the focused element when `open` flips true; restores
// focus to it when `open` flips back false. Skips restoration if
// the captured element is no longer in the DOM (e.g. tab closed
// during the interaction) — sets focus to body in that case.

import { useEffect, useRef } from 'react';

export function useFocusRestore(open: boolean): void {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      // Capture the element that had focus immediately before this
      // surface opened. document.activeElement is HTMLElement-like
      // but typed as Element — check tagName / focus support.
      const active = document.activeElement;
      if (active instanceof HTMLElement) {
        previouslyFocused.current = active;
      }
    } else {
      // Closing — restore focus if we have a target and it's
      // still attached to the document.
      const target = previouslyFocused.current;
      previouslyFocused.current = null;
      if (target && document.contains(target)) {
        // requestAnimationFrame so we don't fight a concurrent
        // setState that may move focus elsewhere.
        requestAnimationFrame(() => target.focus());
      }
    }
  }, [open]);
}
